import styled from 'styled-components'
import { useEffect, useState } from 'react'
import { Typography } from '@healingpaper/cell'

import { Video } from '@/common/models/search'
import Text from '@/components/atomic/atoms/Text'
import TreatmentSummary, { Summary } from '@/components/atomic/organisms/TreatmentSummary'
import Badge from '@/components/cell/molecules/Badge'

export interface Props {
  id: number
  name: string
  description: string
  summaries: Summary[]
  videos?: Video[]
  text: {
    seeMore: string
    seeMoreTreatment: string
  }
  onClickDetail: (id: number) => void
  onClickVideo?: (uri: string) => void
  className?: string
}

const TreatmentCard = ({ name, description, summaries, videos, onClickVideo, className, text }: Props): JSX.Element => {
  return (
    <StyledContainer className={className}>
      <StyledContent>
        <StyledTypography type="title4" $bottom={8} elementType="h3">
          {name}
        </StyledTypography>

        <div>
          <Description>{description}</Description>
        </div>

        {/* 핵심 요약 */}
        {summaries.length > 0 && (
          <StyledSummary>
            <TreatmentSummary title={text.seeMoreTreatment} summaries={summaries} />
          </StyledSummary>
        )}

        {/* 유튜브 영상 - 추후 서버에서 youtube 데이터 내려줄 예정. 현재는 해당 필드 없음 */}
        {videos && videos.length > 0 && (
          <StyledVideoList>
            {videos.map(({ title, subTitle, originUri, playTime, thumbnailImage }) => (
              <StyledVideo key={title} {...(onClickVideo && { onClick: () => onClickVideo(originUri) })}>
                <StyledVideoInfo>
                  <StyledTypography type="caption2" $bottom={2}>
                    {subTitle}
                  </StyledTypography>
                  <div>
                    <StyledTypography type="title4" elementType="h4" $right={4} $inline>
                      {title}
                    </StyledTypography>
                    <Badge size="small">{playTime}</Badge>
                  </div>
                </StyledVideoInfo>

                <img src={thumbnailImage} alt={title} />
              </StyledVideo>
            ))}
          </StyledVideoList>
        )}
      </StyledContent>
    </StyledContainer>
  )
}

const Description = ({ children }: { children: string }) => {
  const [isExpanded, setIsExpanded] = useState(true)
  useEffect(() => {
    setIsExpanded(false)
  }, [])

  return (
    <StyledDescription
      type="description2"
      element="p"
      $clamp={isExpanded ? 'none' : '5'}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      {children}
    </StyledDescription>
  )
}

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.colors.gray400};
  border-radius: 8px;
`
const StyledContent = styled.div`
  flex: 1;
`
const StyledTypography = styled(Typography)<{
  $top?: number
  $right?: number
  $bottom?: number
  $left?: number
  $inline?: boolean
}>`
  ${({ $inline }) => $inline && 'display: inline-block;'}
  ${({ $top }) => $top && `margin-top: ${$top}px;`}
  ${({ $right }) => $right && `margin-right: ${$right}px;`}
  ${({ $bottom }) => $bottom && `margin-bottom: ${$bottom}px;`}
  ${({ $left }) => $left && `margin-left: ${$left}px;`}
`
const StyledDescription = styled(Text)<{ $clamp: string }>`
  display: inline;
  /* ellipsis */
  display: -webkit-box;
  -webkit-line-clamp: ${({ $clamp }) => $clamp};
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`
const StyledSummary = styled.div`
  margin-top: 26px;
`
const StyledVideoList = styled.div`
  margin-top: 16px;
`
const StyledVideoInfo = styled.div`
  span {
    font-size: 10px;
    font-weight: 500;
    color: #666666;
  }
  > div {
    display: flex;
    align-items: center;
  }
`
const StyledVideo = styled.div`
  display: flex;
  padding: 8px 0;
  border-bottom: 1px solid #ececec;
  cursor: pointer;
  &:first-of-type {
    padding-top: 0;
  }
  &:last-of-type {
    padding-bottom: 0;
    border-bottom: none;
  }
  ${StyledVideoInfo} {
    flex: 1;
  }
  img {
    width: 90px;
    height: 50px;
    border-radius: 4px;
  }
`

export default TreatmentCard
